/* ═══════════════════════════════════════════════════════════════════════════
   Client-side rules — the half of `lib/api` that is not a network call
   ───────────────────────────────────────────────────────────────────────────
   Everything here is pure and synchronous, and stays that way whichever
   implementation is live. That distinction is the reason this file exists
   separately from `mock.ts` and `http.ts`.

   The failure it prevents: someone swapping the app onto the real backend sees
   `priceOrder` exported from the API module, assumes it is an endpoint, and
   turns a 60fps recalculation into a round trip per keystroke on the basket
   screen. Splitting the file makes the answer structural — if it is in here it
   never touches the network, and there is no version of it that does.

   Pricing is deliberately duplicated rather than fetched: the cart must
   reprice instantly as quantities change, and the server must reprice
   authoritatively at checkout because a client total is a number the customer
   controls. Two call sites, one rule — the server's answer wins, and
   `placeOrder` compares them so a mismatch surfaces as a conflict rather than
   as a silently wrong invoice.
   ═══════════════════════════════════════════════════════════════════════════ */

import type {
  CartLine,
  CatalogItem,
  CategoryId,
  Money,
  OrderStatus,
  OrderTotals,
  Promo,
  Provider,
  ServiceCategory,
  Venue,
} from '../types'

/** The account `VITE_API_MODE=mock` starts with, so the password screens have
 *  something to sign in to before anything has been registered — already
 *  verified, because every other address the mock knows starts out unverified
 *  and a mode whose only account is stuck at "check your inbox" is a mode
 *  nobody can develop the rest of the app in.
 *
 *  A screen that can only be reached by first completing another screen is a
 *  screen that gets built once and then never looked at again. Revealed by the
 *  sign-in form only when the mock is the thing answering.
 *
 *  There is no `DEV_OTP` beside it any more. Verification is the mailbox, and
 *  what a developer needs in its place is the mock's outbox — see `sentMail`. */
export const DEV_EMAIL = 'demo@hexclean.app'
export const DEV_PASSWORD = 'hexclean'

/** How short a password this app will submit.
 *
 *  Firebase's own floor is six characters and the project may raise it; eight
 *  is what the form asks for, so the common rejection is caught in the field
 *  where it can be fixed rather than after a round trip. It is a floor and not
 *  the policy — the policy lives on the identity project, and a client that
 *  believed it owned the rule would be the client that disagrees with it after
 *  the first console change. */
export const MIN_PASSWORD = 8

/** Cancellation is only offered while the work has not started. Past that the
 *  UI routes to support instead, because a button that fails is worse than a
 *  button that is not there. */
export const CANCELLABLE: readonly OrderStatus[] = ['placed', 'confirmed']

/* ── pricing ──────────────────────────────────────────────────────────────── */

export function lineTotal(line: CartLine, item: CatalogItem): Money {
  const opts = (item.options ?? []).filter((o) => line.optionIds.includes(o.id))
  const each = item.price + opts.reduce((s, o) => s + o.priceDelta, 0)
  return each * line.qty
}

/** The commercial terms behind a list: what it costs to have someone turn up,
 *  and how little you are allowed to order.
 *
 *  Derived rather than fetched, and pure, so the basket footer, the checkout
 *  summary and the eventual server-side re-price all read the same three
 *  numbers out of data every one of them already holds. */
export interface ListTerms {
  categoryId: CategoryId
  minOrder: Money
  deliveryFee: Money
  /** Subtotal above which the fee is waived. Zero means never — the server's
   *  spelling, kept because an `undefined` here is a waiver that never fires
   *  and a `null` is one more thing for a comparison to get wrong. */
  freeDeliveryOver: Money
}

/** The category's terms, always.
 *
 *  This used to swap in a picked partner's own `minOrder` and `deliveryFee`,
 *  reasoning that a shop the customer chose charges its own prices. The server
 *  does not agree and never has: `domain.Price` resolves the category from the
 *  `listId` and prices against it, so a provider's terms were a number this app
 *  displayed and placement then refused.
 *
 *  Realigned rather than argued with, because the premise had also moved.
 *  Zain, 13 Aug 2026: **customers do not pick shops.** The only choice put to
 *  them today is the car wash venue — come to me, or I drive to you — and
 *  whether a service asks for a shop at all is to become a per-service flag set
 *  in the admin panel ("require customer to select a shop"), with the shop
 *  auto-selected and unnamed otherwise. Per-provider pricing therefore waits
 *  for that flag to exist rather than being guessed at here; it is a Phase 5
 *  row, not a Phase 4 one.
 *
 *  `_provider` stays in the signature so the call sites do not churn for a
 *  decision that is coming back. */
export function termsOf(category: ServiceCategory, _provider: Provider | null): ListTerms {
  return {
    categoryId: category.id,
    minOrder: category.minOrder,
    deliveryFee: category.deliveryFee,
    freeDeliveryOver: category.freeDeliveryOver,
  }
}

/** What it costs to have someone turn up, after the two waivers.
 *
 *  The original is `deliveryFee` in `internal/domain/pricing.go`. Neither
 *  waiver existed here, and both fire on ordinary orders: a `you-come` job is
 *  the customer driving to the forecourt, and charging them delivery for it is
 *  a number the server will refuse at placement. */
function feeAfterWaivers(venue: Venue, terms: ListTerms, subtotal: Money): Money {
  if (venue === 'you-come') return 0
  if (terms.freeDeliveryOver > 0 && subtotal >= terms.freeDeliveryOver) return 0
  return terms.deliveryFee
}

/** Saudi VAT in basis points. The original is `VATBasisPoints` in
 *  `internal/domain/money.go`. */
export const VAT_BASIS_POINTS = 1500

/** The VAT contained *within* a VAT-inclusive amount.
 *
 *  Extracted, never added: displayed prices already include it, so SAR 115.00
 *  gross carries SAR 15.00 of VAT and the customer is charged 115.00 either
 *  way. Getting this backwards inflates every total by 15 % — and it is the
 *  natural mistake, because "add VAT" is what the phrase usually means.
 *
 *  The original is `VATIncludedIn` in `internal/domain/money.go`. */
export function vatIncludedIn(gross: Money): Money {
  if (gross <= 0) return 0
  return Math.round((gross * VAT_BASIS_POINTS) / (10_000 + VAT_BASIS_POINTS))
}

export interface PriceInput {
  lines: CartLine[]
  items: CatalogItem[]
  terms: ListTerms
  /** Required, because the fee depends on it: a `you-come` job is the customer
   *  driving to the forecourt and pays no delivery. */
  venue: Venue
  promo: Promo | null
  slotSurcharge: Money
  walletBalance: Money
  useWallet: boolean
}

export function priceOrder(input: PriceInput): OrderTotals {
  const { lines, items, terms, venue, promo, slotSurcharge, walletBalance, useWallet } = input
  const byId = new Map(items.map((i) => [i.id, i]))

  const subtotal = lines.reduce((sum, l) => {
    const item = byId.get(l.itemId)
    return item ? sum + lineTotal(l, item) : sum
  }, 0)

  let deliveryFee = feeAfterWaivers(venue, terms, subtotal)
  let discount = 0

  if (promo && subtotal >= promo.minSpend) {
    if (promo.categoryId === null || promo.categoryId === terms.categoryId) {
      if (promo.kind === 'percent') {
        const raw = Math.round((subtotal * promo.value) / 100)
        discount = promo.cap === null ? raw : Math.min(raw, promo.cap)
      } else if (promo.kind === 'flat') {
        discount = Math.min(promo.value, subtotal)
      } else {
        deliveryFee = 0
      }
    }
  }

  const beforeWallet = Math.max(0, subtotal + deliveryFee + slotSurcharge - discount)
  const walletApplied = useWallet ? Math.min(walletBalance, beforeWallet) : 0

  return {
    subtotal,
    deliveryFee,
    surcharge: slotSurcharge,
    discount,
    walletApplied,
    /* Always zero in mock mode. The mock has no commission table, so it has no
       credit ceiling either and cannot tell the two halves of a balance apart —
       it spends the lot, which is the *refunded* rule applied to everything.
       Reporting it all as `walletApplied` keeps the one screen that reads these
       separately honest about what it knows. The server splits it properly. */
    refundApplied: 0,
    /* On what was sold, not on what the card is charged. Wallet credit reduces
       the payment and not the sale, so netting it off first would understate
       output VAT on every order a customer part-paid from credit — which is a
       filing error rather than a display bug. Same reason the server computes
       it before applying the wallet. */
    vat: vatIncludedIn(beforeWallet),
    /* What the order came to, with the wallet *not* taken off — the server's
       meaning, and the number placement compares against. What the customer
       actually pays is `amountDue` below; the two differ only when credit is
       being spent, which is exactly when getting it wrong costs money. */
    total: beforeWallet,
  }
}

/** What the customer is actually charged.
 *
 *  `total` is what the order came to and `walletApplied` is credit already
 *  held, so the payment is the difference. One function rather than the
 *  subtraction written at each of the three screens that show a price —
 *  Checkout, Orders, OrderTrack — because three copies of an arithmetic rule
 *  is three chances for one of them to say a different number than the card is
 *  charged. */
export function amountDue(totals: OrderTotals): Money {
  return Math.max(0, totals.total - totals.walletApplied - totals.refundApplied)
}
